import { useEffect, useMemo, useState } from 'react';
import { Clock } from 'lucide-react';
import { cn } from '@my/ui';
import { MyIIcon } from '@my/ui/icons';
import type { QueueRow } from '../lib/queue';
import {
  suggestLoads,
  tempBandFor,
  type SuggestDepot,
  type SuggestedLoad,
  type SuggestionType,
  type TempBand,
} from '../lib/suggestLoads';

/** Filter chips — All + the strategies. */
type FilterValue = 'all' | SuggestionType;

const FILTERS: { value: FilterValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sameLocation', label: 'Same Location' },
  { value: 'international', label: 'International' },
  { value: 'furthest', label: 'Furthest' },
  { value: 'clusters', label: 'Clusters' },
];

/** Per-type colour: a dot on the chip + a matching left border on the card. */
const TYPE_DOT: Record<SuggestionType, string> = {
  sameLocation: 'bg-blue-500',
  international: 'bg-violet-500',
  furthest: 'bg-amber-500',
  clusters: 'bg-teal-500',
};
const TYPE_BORDER: Record<SuggestionType, string> = {
  sameLocation: 'border-l-blue-500',
  international: 'border-l-violet-500',
  furthest: 'border-l-amber-500',
  clusters: 'border-l-teal-500',
};

const TYPE_BAND_BORDER: Record<SuggestionType, string> = {
  sameLocation: 'border-blue-500',
  international: 'border-violet-500',
  furthest: 'border-amber-500',
  clusters: 'border-teal-500',
};

/** Destination chip tint per type (the route's far end). */
const TYPE_CHIP: Record<SuggestionType, string> = {
  sameLocation: 'bg-blue-50 text-blue-700 ring-blue-200',
  international: 'bg-violet-50 text-violet-700 ring-violet-200',
  furthest: 'bg-amber-50 text-amber-800 ring-amber-200',
  clusters: 'bg-teal-50 text-teal-700 ring-teal-200',
};

/** Per-task leg: collection, delivery, or a direct (both legs of the order on this load). */
type Leg = 'Col' | 'Del' | 'Direct';
const LEG_STYLE: Record<Leg, { tag: string }> = {
  Col: { tag: 'bg-emerald-100 text-emerald-700' },
  Del: { tag: 'bg-blue-100 text-blue-700' },
  Direct: { tag: 'bg-violet-100 text-violet-700' },
};

/** Temperature-band colour — the dot in front of each task's order number. */
const TEMP_BANDS: TempBand[] = ['Frozen', 'Chilled', 'Warm', 'Ambient'];
const TEMP_BAND_DOT: Record<TempBand, string> = {
  Frozen: 'bg-blue-700',
  Chilled: 'bg-sky-400',
  Warm: 'bg-amber-500',
  Ambient: 'bg-slate-400',
};

/** Order-task-header style label for a row's stop: "Site name, City, CC". */
const stopLabel = (r: QueueRow) =>
  [
    r.role === 'collection' ? r.collectionName : r.deliveryName,
    r.role === 'collection' ? r.collectionCity : r.deliveryCity,
    r.role === 'collection' ? r.collectionCountry : r.deliveryCountry,
  ]
    .filter((p) => p && p !== '—')
    .join(', ') || '—';

/** Processing messages shown in sequence, ≥1s each, before suggestions appear. */
const PROCESSING_STEPS = ['Analysing', 'Thinking', 'Building'] as const;
const PROCESSING_STEP_MS = 1000;

export interface LoadSuggestionsPanelProps {
  open: boolean;
  onClose: () => void;
  depotLabel: string;
  /** Depot city — the route's starting point on each card. */
  depotCity: string;
  dateLabel: string;
  /** Unassigned, in-horizon queue rows — the pool the engine scans. */
  rows: QueueRow[];
  depot: SuggestDepot;
  capacityLm: number;
  /** Create a load from a suggestion's rows (consume seq → create → assign). */
  onCreateLoad: (rows: QueueRow[]) => void;
}

/**
 * Sliding right-hand overlay where MyI proposes loads. Scans the unassigned
 * queue on open, shows a brief "Processing" state, then renders suggestion
 * cards (filterable by strategy) each with a Create-load action.
 */
export function LoadSuggestionsPanel({
  open,
  onClose,
  depotLabel,
  depotCity,
  dateLabel,
  rows,
  depot,
  capacityLm,
  onCreateLoad,
}: LoadSuggestionsPanelProps) {
  const [activeType, setActiveType] = useState<FilterValue>('all');
  const [scanning, setScanning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [suggestions, setSuggestions] = useState<SuggestedLoad[]>([]);

  // Scan when the panel opens: walk the processing messages (≥1s each), then
  // run the engine once the final step has been shown.
  useEffect(() => {
    if (!open) {
      setScanning(false);
      return;
    }
    setScanning(true);
    setSuggestions([]);
    setStepIndex(0);
    const timers = PROCESSING_STEPS.slice(1).map((_, i) =>
      setTimeout(() => setStepIndex(i + 1), (i + 1) * PROCESSING_STEP_MS),
    );
    timers.push(
      setTimeout(() => {
        setSuggestions(suggestLoads({ rows, depot, capacityLm }));
        setScanning(false);
      }, PROCESSING_STEPS.length * PROCESSING_STEP_MS),
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stepLabel = PROCESSING_STEPS[stepIndex];

  const filtered =
    activeType === 'all' ? suggestions : suggestions.filter((s) => s.type === activeType);

  // Loads per strategy — All = total, each strategy = its share of the scan.
  const counts = useMemo(() => {
    const c: Record<FilterValue, number> = {
      all: suggestions.length,
      sameLocation: 0,
      international: 0,
      furthest: 0,
      clusters: 0,
    };
    for (const s of suggestions) c[s.type] += 1;
    return c;
  }, [suggestions]);

  return (
    <aside
      aria-hidden={!open}
      className={cn(
        'absolute inset-y-0 right-0 z-40 flex w-[34rem] max-w-[92%] flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-300 ease-out',
        open ? 'translate-x-0' : 'pointer-events-none translate-x-full',
      )}
    >
      {/* "my i" bar — only the icon closes the panel; it scans then settles to idle. */}
      <div className="flex w-full items-center gap-2.5 border-b border-slate-700 bg-slate-800 px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close suggestions"
          title="Close"
          className="shrink-0 rounded-full p-0.5 transition-colors hover:bg-slate-700"
        >
          <MyIIcon state={scanning ? 'scanning' : 'idle'} className="h-7 w-7" />
        </button>
        <div className="leading-tight">
          <div className="text-xs font-medium text-slate-100">my i</div>
          <div className="text-[10px] text-slate-400">
            {scanning ? `${stepLabel}…` : 'Load suggestions'}
          </div>
        </div>
        <span className="ml-auto whitespace-nowrap text-[9px] text-slate-200">
          {depotLabel} · {dateLabel} · {rows.length} {rows.length === 1 ? 'task' : 'tasks'}
        </span>
      </div>

      {/* Temp-band legend — explains the coloured dot in front of each task. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-slate-200 px-3 py-1.5 text-[10px] text-slate-400">
        <span className="font-medium uppercase tracking-wide">Temp bands</span>
        {TEMP_BANDS.map((b) => (
          <span key={b} className="flex items-center gap-1">
            <span className={cn('h-1.5 w-1.5 rounded-full', TEMP_BAND_DOT[b])} aria-hidden="true" />
            {b}
          </span>
        ))}
      </div>

      {/* Type filter chips. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-slate-200 px-3 py-2">
        {FILTERS.map((f) => {
          const active = activeType === f.value;
          return (
            <button
              key={f.value}
              type="button"
              onClick={() => setActiveType(f.value)}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors',
                active
                  ? 'border-slate-700 bg-slate-700 text-white'
                  : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700',
              )}
            >
              {f.value !== 'all' && (
                <span
                  className={cn('h-1.5 w-1.5 rounded-full', TYPE_DOT[f.value])}
                  aria-hidden="true"
                />
              )}
              {f.label}
              {!scanning && <span className="tabular-nums opacity-60">{counts[f.value]}</span>}
            </button>
          );
        })}
      </div>

      {/* Body: big centered processing icon until cards arrive; then cards rise
          to the top and the processing indicator drops to the bottom. */}
      <div className="min-h-0 flex-1 overflow-auto">
        {filtered.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
            {scanning ? (
              <>
                <MyIIcon state="scanning" className="h-16 w-16" />
                <p key={stepLabel} className="animate-pulse text-sm font-medium text-slate-600">
                  {stepLabel}…
                </p>
                <p className="text-xs text-slate-400">Scanning the queue to suggest loads.</p>
              </>
            ) : (
              <p className="text-sm italic text-slate-400">No load suggestions for this view.</p>
            )}
          </div>
        ) : (
          <div className="space-y-2 p-3">
            {filtered.map((s) => (
              <SuggestionCard
                key={s.id}
                suggestion={s}
                depotCity={depotCity}
                onCreate={onCreateLoad}
              />
            ))}
            {scanning && (
              <div className="flex items-center justify-center gap-2 py-3 text-xs text-slate-400">
                <MyIIcon state="scanning" className="h-6 w-6" />
                Still processing…
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

function SuggestionCard({
  suggestion,
  depotCity,
  onCreate,
}: {
  suggestion: SuggestedLoad;
  depotCity: string;
  onCreate: (rows: QueueRow[]) => void;
}) {
  const rows = suggestion.rows;
  // An order with BOTH a collection and a delivery leg on this load is a Direct.
  const rolesByOrder = new Map<string, Set<string>>();
  for (const r of rows) {
    (rolesByOrder.get(r.orderId) ?? rolesByOrder.set(r.orderId, new Set()).get(r.orderId)!).add(
      r.role,
    );
  }
  const legFor = (r: QueueRow): Leg => {
    if ((rolesByOrder.get(r.orderId)?.size ?? 0) > 1) return 'Direct';
    return r.role === 'collection' ? 'Col' : 'Del';
  };

  const MAX_LINES = 5;
  const visible = rows.slice(0, MAX_LINES);
  const moreCount = rows.length - visible.length;

  return (
    <div
      className={cn(
        'rounded-md border border-l-8 border-slate-300 p-2',
        TYPE_BAND_BORDER[suggestion.type],
        TYPE_BORDER[suggestion.type],
      )}
    >
      <div className="flex items-start justify-between gap-2 border-b border-slate-200 pb-2">
        <div className="min-w-0">
          {/* Route: depot city → destination (chip for region/country, plain
              small text for Same Location's exact address). */}
          <div className="flex min-w-0 items-center gap-1.5 text-[11px]">
            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600 ring-1 ring-slate-200">
              {depotCity}
            </span>
            <span className="shrink-0 text-slate-400">→</span>
            {suggestion.type === 'sameLocation' ? (
              <span className="min-w-0 truncate font-medium text-slate-700">
                {suggestion.title}
              </span>
            ) : (
              <span
                className={cn(
                  'min-w-0 truncate rounded-full px-2 py-0.5 font-medium ring-1',
                  TYPE_CHIP[suggestion.type],
                )}
              >
                {suggestion.title}
              </span>
            )}
          </div>
          {/* <p className="mt-0.5 text-[10px] text-slate-500">
            {suggestion.stops} {suggestion.stops === 1 ? 'stop' : 'stops'}
            {suggestion.distanceKm != null ? ` · ~${Math.round(suggestion.distanceKm)} km` : ''}
          </p> */}
        </div>

        {suggestion.badge ? (
          <span
            className={cn(
              'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
              suggestion.badge === 'full'
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-amber-100 text-amber-800',
            )}
          >
            {suggestion.badge === 'full' ? 'Full load' : 'Near full'}
          </span>
        ) : suggestion.confidence === 'low' ? (
          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
            Partial
          </span>
        ) : null}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1',
            suggestion.bands.length > 1
              ? 'bg-indigo-50 text-indigo-700 ring-indigo-200'
              : 'bg-slate-100 text-slate-600 ring-slate-200',
          )}
        >
          {suggestion.bands.map((b) => (
            <span
              key={b}
              className={cn('h-1.5 w-1.5 rounded-full', TEMP_BAND_DOT[b])}
              aria-hidden="true"
            />
          ))}
          {suggestion.tempProfile}
        </span>
        <p className="mt-0.5 text-[10px] text-slate-500">
          {suggestion.stops} {suggestion.stops === 1 ? 'stop' : 'stops'}
          {suggestion.distanceKm != null ? ` · ~${Math.round(suggestion.distanceKm)} km` : ''}
          {' · '}
          {suggestion.totalLm.toFixed(1)} LM
        </p>
      </div>
      <ul className="mt-2 space-y-1 text-xs">
        {visible.map((r) => {
          const leg = legFor(r);
          const style = LEG_STYLE[leg];
          const band = tempBandFor(r.setPointC);
          return (
            <li key={r.itemId}>
              <div className="flex items-center gap-2">
                <span
                  title={band}
                  className={cn('h-1.5 w-1.5 shrink-0 rounded-full', TEMP_BAND_DOT[band])}
                  aria-hidden="true"
                />
                <span className="shrink-0 text-[10px] font-bold text-slate-700">
                  #{r.orderNumber}
                </span>
                {r.window && (
                  <span className="flex shrink-0 items-center gap-0.5 text-[10px] text-slate-400">
                    <Clock className="h-3 w-3" />
                    {r.window}
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate text-[10px] text-slate-500">
                  {r.customerName} - {stopLabel(r)}
                </span>

                <span className="shrink-0 text-[10px] tabular-nums text-amber-800">
                  {(r.lm ?? 0).toFixed(1)}m
                </span>
                <span
                  className={cn(
                    'w-12 shrink-0 rounded px-1 py-0.5 text-center text-[9px] font-semibold uppercase',
                    style.tag,
                  )}
                >
                  {leg}
                </span>
              </div>
            </li>
          );
        })}
        {moreCount > 0 && (
          <li className="pl-3.5 text-[11px] italic text-slate-400">+{moreCount} more</li>
        )}
      </ul>

      <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2">
        <span className="flex items-center gap-2">
          <FillBar pct={Math.min(100, suggestion.fillPct * 100)} over={suggestion.fillPct > 1} />
          <span className="text-[10px] tabular-nums text-slate-400">
            {suggestion.totalLm.toFixed(2)} / {suggestion.capacityLm.toFixed(2)} LM ·{' '}
            {Math.round(suggestion.fillPct * 100)}%
          </span>
        </span>
        <button
          type="button"
          onClick={() => onCreate(suggestion.rows)}
          className="rounded-sm px-2 py-1 text-xs font-medium text-purple-600 transition-colors hover:bg-purple-50"
        >
          Create load
        </button>
      </div>
    </div>
  );
}

/** Capacity fill bar — mirrors the Loads panel's LmBar (emerald fill, red over). */
function FillBar({ pct, over }: { pct: number; over: boolean }) {
  return (
    <span className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-200">
      <span
        className={cn('block h-full rounded-full', over ? 'bg-red-500' : 'bg-emerald-500')}
        style={{ width: `${pct}%` }}
      />
    </span>
  );
}
